import {
    createContext,
    useCallback,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import type {ProffCompanyCard, SourceInfo} from "../../../types";

/** How the currently open preview was opened. Only "click" survives a pointer leave. */
export type OpenedBy = "hover" | "click";

type PreviewAnchor = {anchorEl: HTMLElement; openedBy: OpenedBy};

/** The one preview open at a time, with its anchor marker. Either a citation
 *  excerpt or a company card, never both. */
export type CitationPreviewState =
    | (PreviewAnchor & {kind: "citation"; source: SourceInfo})
    | (PreviewAnchor & {kind: "company"; company: ProffCompanyCard})
    | null;

export type ClosePreviewOptions = {
    restoreFocus: boolean;
};

export type CitationPreviewContextValue = {
    state: CitationPreviewState;
    openPreview: (
        source: SourceInfo,
        anchorEl: HTMLElement | null,
        openedBy: OpenedBy
    ) => void;
    openCompanyPreview: (
        company: ProffCompanyCard,
        anchorEl: HTMLElement | null,
        openedBy: OpenedBy
    ) => void;
    closePreview: (options?: ClosePreviewOptions) => void;
    /** Starts the grace timer that closes a hover-opened preview. Idempotent. */
    scheduleClose: () => void;
    /** Cancels a pending grace-timer close, for example when the pointer returns. */
    cancelScheduledClose: () => void;
};

const noop: CitationPreviewContextValue = {
    state: null,
    openPreview: () => {},
    openCompanyPreview: () => {},
    closePreview: () => {},
    scheduleClose: () => {},
    cancelScheduledClose: () => {},
};

/** Default value is a safe no-op so a marker outside the provider stays inert. */
export const CitationPreviewContext =
    createContext<CitationPreviewContextValue>(noop);

/** Grace period after the pointer leaves the marker or the panel before a
 *  hover-opened preview closes. Gives the user time to move from the
 *  marker into the panel. */
const CLOSE_GRACE_MS = 200;

/** Holds at most one open citation or company preview for a whole chat view. */
export const CitationPreviewProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [state, setState] = useState<CitationPreviewState>(null);
    const lastAnchorRef = useRef<HTMLElement | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelScheduledClose = useCallback(() => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const closePreview = useCallback(
        ({restoreFocus}: ClosePreviewOptions = {restoreFocus: true}) => {
            setState(null);
            if (restoreFocus) {
                lastAnchorRef.current?.focus();
            }
        },
        []
    );

    const scheduleClose = useCallback(() => {
        if (closeTimerRef.current) return;
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            // A grace-timer close never moves focus away from the page.
            closePreview({restoreFocus: false});
        }, CLOSE_GRACE_MS);
    }, [closePreview]);

    const openPreview = useCallback(
        (source: SourceInfo, anchorEl: HTMLElement | null, openedBy: OpenedBy) => {
            if (!anchorEl) return;
            cancelScheduledClose();
            lastAnchorRef.current = anchorEl;
            setState({kind: "citation", source, anchorEl, openedBy});
        },
        [cancelScheduledClose]
    );

    const openCompanyPreview = useCallback(
        (
            company: ProffCompanyCard,
            anchorEl: HTMLElement | null,
            openedBy: OpenedBy
        ) => {
            if (!anchorEl) return;
            cancelScheduledClose();
            lastAnchorRef.current = anchorEl;
            setState({kind: "company", company, anchorEl, openedBy});
        },
        [cancelScheduledClose]
    );

    const value = useMemo(
        () => ({
            state,
            openPreview,
            openCompanyPreview,
            closePreview,
            scheduleClose,
            cancelScheduledClose,
        }),
        [
            state,
            openPreview,
            openCompanyPreview,
            closePreview,
            scheduleClose,
            cancelScheduledClose,
        ]
    );

    return (
        <CitationPreviewContext.Provider value={value}>
            {children}
        </CitationPreviewContext.Provider>
    );
};
